import {
  // Use the dedicated ANONYMOUS client for the whole consultation flow so uploads
  // always run as `anon` (never inherit a logged-in staff session). See supabase.js.
  supabasePublic as supabase,
  getSessionId,
  getStoredUserId,
  storeUserId,
} from "../lib/supabase";

const IMAGE_VALIDATION_STATUS = Object.freeze({
  PENDING: "pending",
  PROCESSING: "processing",
  VALID: "valid",
  INVALID: "invalid",
  FAILED: "failed",
});

export class ConsultationService {
  // Static variables to prevent race conditions
  static _userCreationPromise = null;
  static _creatingUser = false;
  static _consultationCreationPromises = new Map(); // Key: userId, Value: Promise

  static normalizeUploadError(error, metadata = {}) {
    const normalized =
      error instanceof Error ? error : new Error(error?.message || "Upload failed");

    if (error && typeof error === "object") {
      if (error.code && !normalized.code) normalized.code = error.code;
      if (error.details && !normalized.details)
        normalized.details = error.details;
      if (error.hint && !normalized.hint) normalized.hint = error.hint;
      if (error.statusCode && !normalized.statusCode)
        normalized.statusCode = error.statusCode;
      if (!normalized.originalError && error !== normalized) {
        normalized.originalError = error;
      }
    }

    Object.assign(normalized, metadata);
    return normalized;
  }

  static getUploadErrorMessage(error) {
    const normalized = this.normalizeUploadError(error);

    if (normalized.isFileTooLarge) {
      return "الصورة كبيرة جداً. يرجى اختيار صورة أصغر من 5 ميغابايت بعد الضغط.";
    }

    if (normalized.isInvalidFileType) {
      return "نوع الصورة غير مدعوم. استخدمي JPG أو PNG أو WebP.";
    }

    const codeSuffix = normalized.code ? ` (${normalized.code})` : "";
    const message =
      normalized.message || "حدث خطأ غير متوقع أثناء رفع الصورة.";

    if (normalized.uploadStage === "database") {
      return `فشل حفظ الصورة في قاعدة البيانات${codeSuffix}: ${message}`;
    }

    if (normalized.uploadStage === "storage") {
      return `فشل رفع الصورة إلى التخزين${codeSuffix}: ${message}`;
    }

    if (normalized.uploadStage === "user_lookup") {
      return `فشل تجهيز جلسة رفع الصورة${codeSuffix}: ${message}`;
    }

    return `فشل رفع الصورة${codeSuffix}: ${message}`;
  }

  // Create anonymous user on first visit
  static async createAnonymousUser(channelParams = null) {
    try {
      const sessionId = getSessionId();

      // If user creation is already in progress, wait for it
      if (this._creatingUser && this._userCreationPromise) {
        console.log("⏳ User creation already in progress, waiting...");
        return await this._userCreationPromise;
      }

      // Check if we already have a user ID stored
      const storedUserId = getStoredUserId();
      if (storedUserId) {
        // Verify user still exists in database
        const { data: existingUser, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", storedUserId)
          .maybeSingle(); // Use maybeSingle to handle 0 rows gracefully

        if (existingUser && !error) {
          console.log("👤 Existing user found:", storedUserId);
          return { data: existingUser, error: null };
        } else {
          console.log(
            "⚠️ Stored user not found in database, clearing localStorage and creating new one"
          );
          // Clear stale localStorage data
          localStorage.removeItem("wonder_user_id");
        }
      }

      // Set creation lock and create promise
      this._creatingUser = true;
      this._userCreationPromise = this._doCreateUser(sessionId, channelParams);

      try {
        const result = await this._userCreationPromise;
        return result;
      } finally {
        // Clear the lock
        this._creatingUser = false;
        this._userCreationPromise = null;
      }
    } catch (error) {
      console.error("❌ Error in createAnonymousUser:", error);
      // Clear the lock on error
      this._creatingUser = false;
      this._userCreationPromise = null;
      return { data: null, error };
    }
  }

  // Internal method to actually create the user (separated to avoid race conditions)
  // NOTE: Always creates with is_anonymous: true. n8n will update to false for custom links via webhook.
  static async _doCreateUser(sessionId, channelParams = null) {
    console.log("🆕 Creating new user for session:", sessionId);

    // Log channel info for debugging (n8n will use this to update is_anonymous)
    if (channelParams?.contact_id && channelParams?.channel_id) {
      console.log(
        "🔗 Custom link detected - n8n will update is_anonymous to false:",
        {
          contact_id: channelParams.contact_id,
          channel_id: channelParams.channel_id,
        }
      );
    } else {
      console.log("🔗 General link - is_anonymous will remain true");
    }

    // Check if session already has a user (handle race conditions)
    const { data: existingSessionUser } = await supabase
      .from("users")
      .select("*")
      .eq("session_id", sessionId)
      .maybeSingle();

    if (existingSessionUser) {
      console.log(
        "✅ User already exists for this session:",
        existingSessionUser.id
      );
      storeUserId(existingSessionUser.id);
      return { data: existingSessionUser, error: null };
    }

    // Always create with is_anonymous: true - n8n will update to false for custom links
    const { data, error } = await supabase
      .from("users")
      .insert([
        {
          session_id: sessionId,
          name: null, // Will be filled later
          phone: null, // Will be filled later
          is_anonymous: true, // Always true - n8n handles setting to false for custom links
          created_at: new Date().toISOString(),
        },
      ])
      .select("id, session_id, name, phone, created_at")
      .single();

    if (data && !error) {
      storeUserId(data.id);
      console.log(
        "✅ New user created and stored:",
        data.id,
        "is_anonymous: true (n8n will update if custom link)"
      );
      return { data, error: null };
    } else if (error?.code === "23505") {
      // Handle duplicate session_id error - fetch the existing user
      console.log(
        "⚠️ Session already exists during creation, fetching existing user"
      );
      const { data: duplicateUser } = await supabase
        .from("users")
        .select("id, session_id, name, phone, created_at")
        .eq("session_id", sessionId)
        .maybeSingle();

      if (duplicateUser) {
        storeUserId(duplicateUser.id);
        console.log(
          "✅ Retrieved existing user after duplicate error:",
          duplicateUser.id
        );
        return { data: duplicateUser, error: null };
      }
    }

    console.error("❌ Failed to create user:", error);
    return { data, error };
  }

  // Get current user (create if doesn't exist)
  static async getCurrentUser(channelParams = null) {
    const storedUserId = getStoredUserId();

    if (storedUserId) {
      // Try to get existing user
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", storedUserId)
        .maybeSingle(); // Use maybeSingle to handle 0 rows gracefully

      if (data && !error) {
        console.log("👤 Current user retrieved:", data.id);
        return { data, error: null };
      } else {
        console.log(
          "⚠️ Stored user ID invalid, clearing and creating new user"
        );
        localStorage.removeItem("wonder_user_id");
      }
    }

    // Create new user if none exists
    console.log("🔄 No current user found, creating new user");
    return await this.createAnonymousUser(channelParams);
  }

  // Get user by session token (for Google Sheet form flow / FormFollowUp component)
  // IMPORTANT: This method is ONLY for FormFollowUp flow where users are pre-created by n8n
  // It will NEVER create a new user - it only retrieves existing users
  static async getUserBySessionToken(sessionToken) {
    try {
      // console.log(
      //   "🔍 [FormFollowUp] Loading user by session token:",
      //   sessionToken
      // );

      // Validate session token format (should be gsheet_...)
      if (!sessionToken.startsWith("gsheet_")) {
        console.warn(
          "⚠️ [FormFollowUp] Unexpected session token format (expected gsheet_):",
          sessionToken
        );
      }

      const { data, error } = await supabase
        .from("users")
        .select(
          `
          *,
          user_consultations (*),
          consultation_images (*)
        `
        )
        .eq("session_id", sessionToken)
        .maybeSingle();

      if (error) {
        console.error(
          "❌ [FormFollowUp] Error loading user by session token:",
          error
        );
        return { data: null, error };
      }

      if (!data) {
        console.error(
          "❌ [FormFollowUp] No user found with session token:",
          sessionToken
        );
        console.error(
          "   This user should have been created by n8n before sending the link"
        );
        return { data: null, error: new Error("User not found") };
      }

      console.log(
        "✅ [FormFollowUp] User loaded successfully:",
        data.id,
        "name:",
        data.name,
        "phone:",
        data.phone
      );
      console.log(
        "   Session ID:",
        data.session_id,
        "| is_anonymous:",
        data.is_anonymous
      );
      // console.log(
      //   "🔍 [DEBUG] Raw Supabase data:",
      //   JSON.stringify(data, null, 2)
      // );
      // console.log(
      //   "🔍 [DEBUG] data.user_consultations type:",
      //   typeof data.user_consultations
      // );
      // console.log(
      //   "🔍 [DEBUG] data.user_consultations value:",
      //   data.user_consultations
      // );
      // console.log(
      //   "🔍 [DEBUG] Is array?:",
      //   Array.isArray(data.user_consultations)
      // );

      // Handle both array and object formats for user_consultations
      let consultation = null;
      if (data.user_consultations) {
        if (Array.isArray(data.user_consultations)) {
          // If it's an array, take the first element
          consultation = data.user_consultations[0] || null;
        } else {
          // If it's an object, use it directly
          consultation = data.user_consultations;
        }
      }

      // console.log("🔍 [DEBUG] Final consultation object:", consultation);
      // console.log("🔍 [DEBUG] consultation.budget:", consultation?.budget);

      return {
        data: {
          user: data,
          consultation: consultation,
          images: data.consultation_images || [],
        },
        error: null,
      };
    } catch (error) {
      console.error(
        "❌ [FormFollowUp] Exception in getUserBySessionToken:",
        error
      );
      return { data: null, error };
    }
  }

  // Create or get user
  static async createOrGetUser(userData) {
    try {
      const sessionId = getSessionId();

      // First check if user already exists for this session
      const { data: existingUser, error: fetchError } = await supabase
        .from("users")
        .select("*")
        .eq("session_id", sessionId)
        .maybeSingle(); // Use maybeSingle to handle 0 rows gracefully

      if (existingUser && !fetchError) {
        // Update existing user if data has changed
        const { data, error } = await supabase
          .from("users")
          .update({
            name: userData.name,
            phone: userData.phone,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingUser.id)
          .select()
          .single();

        return { data: data || existingUser, error };
      }

      // Create new user
      const { data, error } = await supabase
        .from("users")
        .insert([
          {
            session_id: sessionId,
            name: userData.name,
            phone: userData.phone,
            is_anonymous: true,
          },
        ])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error("Error in createOrGetUser:", error);
      return { data: null, error };
    }
  }

  // Create or update consultation
  static async createOrUpdateConsultation(userId, consultationData) {
    try {
      // Check if consultation creation is already in progress for this user
      if (this._consultationCreationPromises.has(userId)) {
        console.log(
          "⏳ Consultation creation already in progress for user:",
          userId
        );
        return await this._consultationCreationPromises.get(userId);
      }

      // Create promise and store it
      const consultationPromise = this._doCreateOrUpdateConsultation(
        userId,
        consultationData
      );
      this._consultationCreationPromises.set(userId, consultationPromise);

      try {
        const result = await consultationPromise;
        return result;
      } finally {
        // Clear the promise
        this._consultationCreationPromises.delete(userId);
      }
    } catch (error) {
      console.error("Error in createOrUpdateConsultation:", error);
      this._consultationCreationPromises.delete(userId);
      return { data: null, error };
    }
  }

  // Internal method for consultation creation/update
  static async _doCreateOrUpdateConsultation(userId, consultationData) {
    try {
      // Check if consultation already exists
      const { data: existingConsultation, error: fetchError } = await supabase
        .from("user_consultations")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle(); // Use maybeSingle to handle 0 rows gracefully

      // Prepare consultation payload
      const consultationPayload = {
        user_id: userId,
        skin_type: consultationData.skinType,
        relationship_status: consultationData.relationshipStatus,
        skin_sensitivity: consultationData.notSensitive
          ? []
          : consultationData.skinSensitivity,
        additional_sensitivity: consultationData.additionalSensitivity,
        updated_at: new Date().toISOString(),
      };

      // Check if we're trying to update with empty data
      const hasValidData =
        consultationData.skinType ||
        consultationData.relationshipStatus ||
        (consultationData.skinSensitivity &&
          consultationData.skinSensitivity.length > 0) ||
        consultationData.additionalSensitivity;

      // If consultation exists but we have no valid new data, don't update
      if (existingConsultation && !hasValidData) {
        console.log(
          "⚠️ Skipping consultation update - no valid new data provided"
        );
        return { data: existingConsultation, error: null };
      }

      if (existingConsultation && !fetchError) {
        // Update existing consultation
        const { data, error } = await supabase
          .from("user_consultations")
          .update(consultationPayload)
          .eq("id", existingConsultation.id)
          .select()
          .single();

        return { data, error };
      }

      // Create new consultation
      const { data, error } = await supabase
        .from("user_consultations")
        .insert([consultationPayload])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error("Error in _doCreateOrUpdateConsultation:", error);
      return { data: null, error };
    }
  }

  static generateImageValidationRequestId() {
    const browserCrypto =
      typeof window !== "undefined" && window.crypto
        ? window.crypto
        : null;

    if (browserCrypto && typeof browserCrypto.randomUUID === "function") {
      return browserCrypto.randomUUID();
    }

    return `image_validation_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  static async prepareImageValidationForUser(userId, consultationData = {}) {
    try {
      if (!userId) {
        return {
          data: null,
          error: new Error("User ID is required for image validation"),
        };
      }

      const consultationResult = await this.createOrUpdateConsultation(
        userId,
        consultationData
      );

      if (consultationResult.error || !consultationResult.data) {
        return {
          data: null,
          error:
            consultationResult.error ||
            new Error("Failed to create consultation for image validation"),
        };
      }

      const imageValidationRequestId = this.generateImageValidationRequestId();
      const timestamp = new Date().toISOString();

      const { data, error } = await supabase
        .from("user_consultations")
        .update({
          image_validation_status: IMAGE_VALIDATION_STATUS.PROCESSING,
          image_validation_decision: null,
          image_validation_request_id: imageValidationRequestId,
          image_validation_started_at: timestamp,
          image_validation_completed_at: null,
          updated_at: timestamp,
        })
        .eq("id", consultationResult.data.id)
        .select()
        .single();

      if (error || !data) {
        console.error("❌ Failed to prepare image validation state:", error);
        return {
          data: null,
          error: error || new Error("Failed to prepare image validation state"),
        };
      }

      return {
        data: {
          consultation: data,
          imageValidationRequestId,
        },
        error: null,
      };
    } catch (error) {
      console.error("❌ Error in prepareImageValidationForUser:", error);
      return { data: null, error };
    }
  }

  // Upload image with user_id (no consultation required)
  // NOTE: This method is for the MAIN consultation flow (WonderBeautiesConsultation)
  // It creates users on the fly using getCurrentUser() which generates browser session IDs
  // For FormFollowUp flow, use uploadImageForUser() instead to prevent creating duplicate users
  static async uploadSessionImage(imageFile, channelParams = null) {
    try {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(imageFile.type)) {
        const error = this.normalizeUploadError(
          new Error(
            `File type ${imageFile.type} not allowed. Use JPG, PNG, or WebP.`
          ),
          {
            isInvalidFileType: true,
            uploadStage: "validation",
          }
        );
        console.error("❌ Invalid file type:", error.message);
        return { data: null, error };
      }

      // Compress image if it's too large
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit after compression
      let processedFile = imageFile;

      if (imageFile.size > MAX_FILE_SIZE) {
        console.log(
          "📦 Image is large, compressing...",
          (imageFile.size / (1024 * 1024)).toFixed(1) + "MB"
        );
        processedFile = await this._compressImage(imageFile, 0.7); // 70% quality

        if (processedFile.size > MAX_FILE_SIZE) {
          // If still too large after compression
          const sizeMB = (processedFile.size / (1024 * 1024)).toFixed(1);
          const error = this.normalizeUploadError(
            new Error(
              `Image too large (${sizeMB}MB) even after compression. Please use a smaller image.`
            ),
            {
              isFileTooLarge: true,
              uploadStage: "validation",
            }
          );
          console.error("❌ File too large after compression:", error.message);
          return { data: null, error };
        }

        const originalMB = (imageFile.size / (1024 * 1024)).toFixed(1);
        const compressedMB = (processedFile.size / (1024 * 1024)).toFixed(1);
        console.log(
          "✅ Image compressed:",
          `${originalMB}MB → ${compressedMB}MB`
        );
      }

      // Get or create current user (with channel params for proper is_anonymous setting)
      const userResult = await this.getCurrentUser(channelParams);
      if (userResult.error || !userResult.data) {
        console.error("❌ Failed to get current user for image upload");
        return {
          data: null,
          error: this.normalizeUploadError(
            userResult.error || new Error("No user available"),
            { uploadStage: "user_lookup" }
          ),
        };
      }

      const userId = userResult.data.id;
      const sizeMB = (processedFile.size / (1024 * 1024)).toFixed(1);
      console.log(
        "🔄 Starting image upload for user:",
        userId,
        "file:",
        processedFile.name || imageFile.name,
        `(${sizeMB}MB)`
      );

      const fileExt = imageFile.name.split(".").pop();
      const fileName = `user_${userId}_${Date.now()}.${fileExt}`;
      const filePath = `consultation-images/${fileName}`;

      // Upload to Supabase storage
      console.log("📤 Uploading to storage:", filePath);
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("consultation-images")
        .upload(filePath, processedFile);

      if (uploadError) {
        console.error("❌ Storage upload error:", uploadError);
        return {
          data: null,
          error: this.normalizeUploadError(uploadError, {
            uploadStage: "storage",
          }),
        };
      }

      console.log("✅ Storage upload successful:", uploadData);

      // Save image reference with user_id (no consultation_id yet)
      const imageRecord = {
        user_id: userId, // Use user_id instead of session_id
        consultation_id: null, // Will be linked later
        image_path: uploadData.path,
        image_type: "skin_analysis",
        file_size: processedFile.size,
        mime_type: processedFile.type || imageFile.type,
      };

      console.log("💾 Saving image record to database:", imageRecord);
      const { data, error } = await supabase
        .from("consultation_images")
        .insert([imageRecord])
        .select()
        .single();

      if (error) {
        console.error("❌ Database insert error:", error);
        return {
          data: null,
          error: this.normalizeUploadError(error, {
            uploadStage: "database",
          }),
        };
      }

      console.log("✅ Database insert successful:", data);
      return { data, error };
    } catch (error) {
      console.error("❌ Error in uploadSessionImage:", error);
      return {
        data: null,
        error: this.normalizeUploadError(error, {
          uploadStage: error?.uploadStage || "unexpected",
        }),
      };
    }
  }

  // Upload image for a SPECIFIC user (used in FormFollowUp to prevent creating new users)
  // This method explicitly takes a userId and will NEVER create a new user
  static async uploadImageForUser(imageFile, userId, channelParams = null) {
    try {
      console.log(
        "📤 [FormFollowUp] Uploading image for existing user:",
        userId
      );

      // Validate that userId is provided
      if (!userId) {
        const error = this.normalizeUploadError(
          new Error("User ID is required for FormFollowUp image upload"),
          { uploadStage: "user_lookup" }
        );
        console.error("❌ No user ID provided:", error.message);
        return { data: null, error };
      }

      // Verify user exists in database
      const { data: existingUser, error: userError } = await supabase
        .from("users")
        .select("id, name, session_id")
        .eq("id", userId)
        .maybeSingle();

      if (userError || !existingUser) {
        const error = this.normalizeUploadError(
          new Error("User not found in database. Cannot upload image."),
          { uploadStage: "user_lookup" }
        );
        console.error("❌ User verification failed:", userId, userError);
        return { data: null, error };
      }

      console.log(
        "✅ User verified:",
        existingUser.id,
        "session:",
        existingUser.session_id
      );

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(imageFile.type)) {
        const error = this.normalizeUploadError(
          new Error(
            `File type ${imageFile.type} not allowed. Use JPG, PNG, or WebP.`
          ),
          {
            isInvalidFileType: true,
            uploadStage: "validation",
          }
        );
        console.error("❌ Invalid file type:", error.message);
        return { data: null, error };
      }

      // Compress image if it's too large
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit after compression
      let processedFile = imageFile;

      if (imageFile.size > MAX_FILE_SIZE) {
        console.log(
          "📦 Image is large, compressing...",
          (imageFile.size / (1024 * 1024)).toFixed(1) + "MB"
        );
        processedFile = await this._compressImage(imageFile, 0.7); // 70% quality

        if (processedFile.size > MAX_FILE_SIZE) {
          const sizeMB = (processedFile.size / (1024 * 1024)).toFixed(1);
          const error = this.normalizeUploadError(
            new Error(
              `Image too large (${sizeMB}MB) even after compression. Please use a smaller image.`
            ),
            {
              isFileTooLarge: true,
              uploadStage: "validation",
            }
          );
          console.error("❌ File too large after compression:", error.message);
          return { data: null, error };
        }

        const originalMB = (imageFile.size / (1024 * 1024)).toFixed(1);
        const compressedMB = (processedFile.size / (1024 * 1024)).toFixed(1);
        console.log(
          "✅ Image compressed:",
          `${originalMB}MB → ${compressedMB}MB`
        );
      }

      const sizeMB = (processedFile.size / (1024 * 1024)).toFixed(1);
      console.log(
        "🔄 Starting image upload for user:",
        userId,
        "file:",
        processedFile.name || imageFile.name,
        `(${sizeMB}MB)`
      );

      const fileExt = imageFile.name.split(".").pop();
      const fileName = `user_${userId}_${Date.now()}.${fileExt}`;
      const filePath = `consultation-images/${fileName}`;

      // Upload to Supabase storage
      console.log("📤 Uploading to storage:", filePath);
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("consultation-images")
        .upload(filePath, processedFile);

      if (uploadError) {
        console.error("❌ Storage upload error:", uploadError);
        return {
          data: null,
          error: this.normalizeUploadError(uploadError, {
            uploadStage: "storage",
          }),
        };
      }

      console.log("✅ Storage upload successful:", uploadData);

      // Save image reference with user_id (no consultation_id yet)
      const imageRecord = {
        user_id: userId, // Use the explicitly provided user ID
        consultation_id: null, // Will be linked later
        image_path: uploadData.path,
        image_type: "skin_analysis",
        file_size: processedFile.size,
        mime_type: processedFile.type || imageFile.type,
      };

      console.log(
        "💾 [FormFollowUp] Saving image record to database:",
        imageRecord
      );
      const { data, error } = await supabase
        .from("consultation_images")
        .insert([imageRecord])
        .select()
        .single();

      if (error) {
        console.error("❌ Database insert error:", error);
        return {
          data: null,
          error: this.normalizeUploadError(error, {
            uploadStage: "database",
          }),
        };
      }

      console.log(
        "✅ [FormFollowUp] Image successfully linked to user:",
        userId,
        data
      );
      return { data, error: null };
    } catch (error) {
      console.error("❌ Error in uploadImageForUser:", error);
      return {
        data: null,
        error: this.normalizeUploadError(error, {
          uploadStage: error?.uploadStage || "unexpected",
        }),
      };
    }
  }

  // Image compression utility
  static async _compressImage(file, quality = 0.7) {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions (max 1920x1920 for skin analysis)
        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1920;

        let { width, height } = img;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = (height * MAX_WIDTH) / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = (width * MAX_HEIGHT) / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg", // Always convert to JPEG for better compression
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  }

  // Upload image to storage and save reference
  static async uploadConsultationImage(consultationId, imageFile) {
    try {
      const fileExt = imageFile.name.split(".").pop();
      const fileName = `${consultationId}_${Date.now()}.${fileExt}`;
      const filePath = `consultation-images/${fileName}`;

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("consultation-images")
        .upload(filePath, imageFile);

      if (uploadError) {
        return { data: null, error: uploadError };
      }

      // Save image reference in database
      const { data, error } = await supabase
        .from("consultation_images")
        .insert([
          {
            consultation_id: consultationId,
            image_path: uploadData.path,
            image_type: "skin_analysis",
            file_size: imageFile.size,
            mime_type: imageFile.type,
          },
        ])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error("Error in uploadConsultationImage:", error);
      return { data: null, error };
    }
  }

  // Load existing consultation data for current user
  static async loadExistingConsultationData(channelParams = null) {
    try {
      // Use channel params to ensure we find the correct user (don't create new one)
      const userResult = await this.getCurrentUser(channelParams);
      if (userResult.error || !userResult.data) {
        return { data: null, error: null }; // No user = no data
      }

      const userId = userResult.data.id;
      console.log("📥 Loading existing data for user:", userId);

      // Get user with consultation and images
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select(
          `
          *,
          user_consultations (*),
          consultation_images (*)
        `
        )
        .eq("id", userId)
        .maybeSingle(); // Use maybeSingle to handle 0 rows gracefully

      if (userError) {
        console.log("ℹ️ No existing consultation data found");
        return {
          data: { user: userResult.data, consultation: null, images: [] },
          error: null,
        };
      }

      return {
        data: {
          user: userData,
          consultation: userData.user_consultations?.[0] || null,
          images: userData.consultation_images || [],
        },
        error: null,
      };
    } catch (error) {
      console.error("❌ Error loading existing consultation data:", error);
      return { data: null, error };
    }
  }

  // Save complete consultation and link user images
  static async saveCompleteConsultation(
    formData,
    loadOnly = false,
    channelParams = null
  ) {
    try {
      // If loadOnly flag is true, just return existing data WITHOUT any updates
      if (loadOnly) {
        // console.log(
        //   "🔍 Load-only mode: fetching existing data without updates"
        // );
        const existingData = await this.loadExistingConsultationData(
          channelParams
        );
        // Ensure we return a consistent structure even if data is null
        return {
          success: true,
          data: existingData.data || null,
          error: existingData.error || null,
        };
      }

      // 1. Get current user (should already exist from image upload)
      // Channel params passed here for consistency, but user should already exist
      const userResult = await this.getCurrentUser(channelParams);
      if (userResult.error || !userResult.data) {
        console.error("❌ No current user found for consultation save");
        return {
          data: null,
          error: userResult.error || new Error("No user available"),
        };
      }

      // 2. Update user with form data (only if provided)
      // IMPORTANT: Preserve is_anonymous value - n8n manages this (sets to false for custom links)
      let updatedUser = userResult.data;
      if (formData.name || formData.phone) {
        // Preserve the existing is_anonymous value (n8n sets it to false for custom links)
        const currentIsAnonymous = userResult.data.is_anonymous;

        const { data: updateResult, error: updateUserError } = await supabase
          .from("users")
          .update({
            name: formData.name,
            phone: formData.phone,
            is_anonymous: currentIsAnonymous, // Preserve existing value
            updated_at: new Date().toISOString(),
          })
          .eq("id", userResult.data.id)
          .select(
            "id, name, phone, is_anonymous, updated_at, created_at, session_id"
          ) // Explicit select to avoid RLS issues
          .single();

        if (updateUserError) {
          console.error("❌ Failed to update user:", updateUserError);
          console.error("❌ Update error details:", {
            message: updateUserError.message,
            code: updateUserError.code,
            details: updateUserError.details,
            hint: updateUserError.hint,
            userId: userResult.data.id,
            formData: { name: formData.name, phone: formData.phone }, // Log what we tried to save
          });
          return { data: null, error: updateUserError };
        }

        updatedUser = updateResult;
        console.log(
          "✅ User updated with form data:",
          updatedUser.id,
          "is_anonymous preserved:",
          updatedUser.is_anonymous
        );
      }

      // 3. Create or update consultation
      const consultationResult = await this.createOrUpdateConsultation(
        userResult.data.id,
        formData
      );
      if (consultationResult.error) {
        return { data: null, error: consultationResult.error };
      }

      console.log(
        "✅ Consultation created/updated:",
        consultationResult.data.id
      );

      // VERIFICATION: Double-check consultation actually exists in database
      const { data: verifyConsultation, error: verifyError } = await supabase
        .from("user_consultations")
        .select("*")
        .eq("id", consultationResult.data.id)
        .maybeSingle();

      if (!verifyConsultation || verifyError) {
        console.error(
          "❌ Consultation verification failed - data not found in database after save"
        );
        return {
          data: null,
          error: new Error(
            "Failed to verify consultation was saved to database"
          ),
        };
      }

      console.log(
        "✅ Consultation verified in database:",
        verifyConsultation.id
      );

      // 4. Link any unlinked user images to this consultation
      const { error: linkError } = await supabase
        .from("consultation_images")
        .update({ consultation_id: consultationResult.data.id })
        .eq("user_id", userResult.data.id)
        .is("consultation_id", null);

      if (linkError) {
        console.warn("⚠️ Failed to link user images:", linkError);
        // Don't fail the whole operation for this
      } else {
        console.log("✅ User images linked to consultation");
      }

      return {
        success: true,
        data: {
          user: updatedUser,
          consultation: consultationResult.data,
        },
        error: null,
      };
    } catch (error) {
      console.error("❌ Error in saveCompleteConsultation:", error);
      return { data: null, error };
    }
  }

  // Get consultation data for current session (including session-based images)
  static async getConsultationData() {
    try {
      const sessionId = getSessionId();

      // Get user and consultation data - use maybeSingle() instead of single()
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select(
          `
          *,
          user_consultations (
            *,
            consultation_images (*),
            hair_user_consultations!hair_user_consultations_user_consultation_id_fkey (*)
          )
        `
        )
        .eq("session_id", sessionId)
        .maybeSingle(); // This allows 0 or 1 rows (fixes 406 error)

      // If no user found, that's okay for new visitors
      if (!userData && userError?.code === "PGRST116") {
        // Get session-based images even if no user record exists yet
        const { data: sessionImages } = await supabase
          .from("consultation_images")
          .select("*")
          .eq("session_id", sessionId)
          .is("consultation_id", null);

        return {
          data: {
            session_images: sessionImages || [],
            isNewUser: true,
          },
          error: null,
        };
      }

      if (userError) {
        throw userError;
      }

      // Get session-based images (not linked to consultation yet)
      const { data: sessionImages } = await supabase
        .from("consultation_images")
        .select("*")
        .eq("session_id", sessionId)
        .is("consultation_id", null);

      // Combine data
      const result = {
        ...userData,
        session_images: sessionImages || [],
      };

      return { data: result, error: null };
    } catch (error) {
      console.error("Error in getConsultationData:", error);
      return { data: null, error };
    }
  }

  // Update consultation with AI analysis results
  static async updateConsultationAnalysis(consultationId, analysisData) {
    try {
      const { data, error } = await supabase
        .from("user_consultations")
        .update({
          detailed_diagnosis: analysisData.diagnosis,
          dermatologist_analysis: analysisData.dermatologist_analysis,
          products_offered: analysisData.products,
          consultation_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", consultationId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error("Error in updateConsultationAnalysis:", error);
      return { data: null, error };
    }
  }

  // Update consultation budget with DB verification (for FormFollowUp)
  // Matches the verification pattern from saveCompleteConsultation
  static async updateConsultationBudget(userId, budgetLabel) {
    try {
      console.log(
        "💾 Updating consultation budget for user:",
        userId,
        "Budget:",
        budgetLabel
      );

      // 1. Get existing consultation
      const { data: existingConsultation, error: fetchError } = await supabase
        .from("user_consultations")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (fetchError) {
        console.error("❌ Error fetching consultation:", fetchError);
        return { success: false, data: null, error: fetchError };
      }

      if (!existingConsultation) {
        console.error("❌ No consultation found for user:", userId);
        return {
          success: false,
          data: null,
          error: new Error("Consultation not found for user"),
        };
      }

      // 2. Update budget field
      const { data: updatedConsultation, error: updateError } = await supabase
        .from("user_consultations")
        .update({
          budget: budgetLabel,
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingConsultation.id)
        .select()
        .single();

      if (updateError || !updatedConsultation) {
        console.error("❌ Error updating budget:", updateError);
        return {
          success: false,
          data: null,
          error: updateError || new Error("Failed to update budget"),
        };
      }

      console.log("✅ Budget updated:", updatedConsultation.id);

      // 3. VERIFICATION: Double-check budget actually exists in database
      const { data: verifyConsultation, error: verifyError } = await supabase
        .from("user_consultations")
        .select("budget")
        .eq("id", updatedConsultation.id)
        .maybeSingle();

      if (!verifyConsultation || verifyError) {
        console.error(
          "❌ Budget verification failed - data not found in database after save"
        );
        return {
          success: false,
          data: null,
          error: new Error("Failed to verify budget was saved to database"),
        };
      }

      if (verifyConsultation.budget !== budgetLabel) {
        console.error("❌ Budget verification failed - value mismatch");
        console.error("  Expected:", budgetLabel);
        console.error("  Found:", verifyConsultation.budget);
        return {
          success: false,
          data: null,
          error: new Error("Budget verification failed - value mismatch"),
        };
      }

      console.log("✅ Budget verified in database:", verifyConsultation.budget);

      return {
        success: true,
        data: updatedConsultation,
        error: null,
      };
    } catch (error) {
      console.error("❌ Exception in updateConsultationBudget:", error);
      return { success: false, data: null, error };
    }
  }

  // Poll for dermatologist analysis results
  static async pollForDermatologistAnalysis(userId, timeout = 180000) {
    // 3 minutes default
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const pollInterval = 3000; // 3 seconds

      // console.log(
      //   "🔍 Starting dermatologist analysis polling for user:",
      //   userId
      // );

      const checkAnalysis = async () => {
        try {
          // Check if timeout reached
          if (Date.now() - startTime >= timeout) {
            console.log("⏰ Dermatologist analysis polling timeout reached");
            resolve({
              data: null,
              error: null,
              timeout: true,
              timeElapsed: Date.now() - startTime,
            });
            return;
          }

          // Query for dermatologist analysis (Arabic only)
          const { data, error } = await supabase
            .from("user_consultations")
            .select("dermatologist_analysis_ar, id")
            .eq("user_id", userId)
            .not("dermatologist_analysis_ar", "is", null)
            .maybeSingle();

          if (error) {
            console.error("❌ Error polling dermatologist analysis:", error);
            reject(error);
            return;
          }

          if (data && data.dermatologist_analysis_ar) {
            console.log("✅ Dermatologist analysis found (Arabic):", data.id);

            // Only use Arabic structured data
            const analysisData = data.dermatologist_analysis_ar;

            resolve({
              data: analysisData,
              error: null,
              consultationId: data.id,
              timeElapsed: Date.now() - startTime,
            });
            return;
          }

          // Schedule next check
          setTimeout(checkAnalysis, pollInterval);
        } catch (error) {
          console.error("❌ Error in dermatologist analysis polling:", error);
          reject(error);
        }
      };

      // Start polling
      checkAnalysis();
    });
  }

  // Poll for products offered results
  static async pollForProductsOffered(userId, timeout = 180000) {
    // 3 minutes default
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const pollInterval = 3000; // 3 seconds

      console.log("🛍️ Starting products polling for user:", userId);

      const checkProducts = async () => {
        try {
          // Check if timeout reached
          if (Date.now() - startTime >= timeout) {
            console.log("⏰ Products polling timeout reached");
            resolve({
              data: null,
              error: null,
              timeout: true,
              timeElapsed: Date.now() - startTime,
            });
            return;
          }

          // Query for products offered
          const { data, error } = await supabase
            .from("user_consultations")
            .select("products_offered, id, consultation_completed")
            .eq("user_id", userId)
            .not("products_offered", "is", null)
            .maybeSingle();

          if (error) {
            console.error("❌ Error polling products:", error);
            reject(error);
            return;
          }

          if (data && data.products_offered) {
            console.log("✅ Products found:", data.id);
            resolve({
              data: data.products_offered,
              error: null,
              consultationId: data.id,
              consultationCompleted: data.consultation_completed,
              timeElapsed: Date.now() - startTime,
            });
            return;
          }

          // Schedule next check
          setTimeout(checkProducts, pollInterval);
        } catch (error) {
          console.error("❌ Error in products polling:", error);
          reject(error);
        }
      };

      // Start polling
      checkProducts();
    });
  }

  // Combined polling method for both analysis and products
  static async pollForConsultationResults(userId, timeout = 180000) {
    try {
      console.log(
        "🚀 Starting combined consultation polling for user:",
        userId
      );

      // Phase 1: Wait for dermatologist analysis
      const dermaResult = await this.pollForDermatologistAnalysis(
        userId,
        timeout
      );

      if (dermaResult.timeout) {
        return {
          phase: "dermatologist",
          timeout: true,
          timeElapsed: dermaResult.timeElapsed,
        };
      }

      if (dermaResult.error) {
        return {
          phase: "dermatologist",
          error: dermaResult.error,
        };
      }

      // Phase 2: Wait for products (with remaining timeout)
      const remainingTimeout = timeout - dermaResult.timeElapsed;
      if (remainingTimeout <= 0) {
        return {
          phase: "dermatologist",
          timeout: true,
          timeElapsed: dermaResult.timeElapsed,
          dermatologistAnalysis: dermaResult.data,
        };
      }

      const productsResult = await this.pollForProductsOffered(
        userId,
        remainingTimeout
      );

      if (productsResult.timeout) {
        return {
          phase: "products",
          timeout: true,
          timeElapsed: dermaResult.timeElapsed + productsResult.timeElapsed,
          dermatologistAnalysis: dermaResult.data,
        };
      }

      if (productsResult.error) {
        return {
          phase: "products",
          error: productsResult.error,
          dermatologistAnalysis: dermaResult.data,
        };
      }

      // Success - both phases completed
      return {
        phase: "completed",
        dermatologistAnalysis: dermaResult.data,
        products: productsResult.data,
        consultationId: productsResult.consultationId,
        consultationCompleted: productsResult.consultationCompleted,
        totalTimeElapsed: dermaResult.timeElapsed + productsResult.timeElapsed,
      };
    } catch (error) {
      console.error("❌ Error in combined consultation polling:", error);
      return { error };
    }
  }

  // Check for product updates
  static async checkForProductUpdates(userId, lastUpdatedAt = null) {
    try {
      // console.log("🔍 Checking for product updates for user:", userId);

      const { data, error } = await supabase
        .from("user_consultations")
        .select("products_offered, updated_at, id")
        .eq("user_id", userId)
        .not("products_offered", "is", null)
        .maybeSingle();

      if (error) {
        console.error("❌ Error checking for product updates:", error);
        return { hasUpdates: false, error };
      }

      if (!data) {
        console.log("ℹ️ No consultation with products found");
        return { hasUpdates: false, error: null };
      }

      // If no lastUpdatedAt provided, assume there are updates
      if (!lastUpdatedAt) {
        console.log("✅ No previous timestamp, returning current data");
        return {
          hasUpdates: true,
          latestData: data,
          error: null,
        };
      }

      // Compare timestamps
      const dbTimestamp = new Date(data.updated_at).getTime();
      const localTimestamp = new Date(lastUpdatedAt).getTime();

      const hasUpdates = dbTimestamp > localTimestamp;

      console.log("📊 Update check result:", {
        hasUpdates,
        dbTime: data.updated_at,
        localTime: lastUpdatedAt,
      });

      return {
        hasUpdates,
        latestData: hasUpdates ? data : null,
        error: null,
      };
    } catch (error) {
      console.error("❌ Error in checkForProductUpdates:", error);
      return { hasUpdates: false, error };
    }
  }

  // Get fresh consultation data (for updating products)
  static async getFreshConsultationData(userId) {
    try {
      console.log("🔄 Fetching fresh consultation data for user:", userId);

      const { data: userData, error } = await supabase
        .from("users")
        .select(
          `
          *,
          user_consultations (
            *,
            hair_user_consultations!hair_user_consultations_user_consultation_id_fkey (*)
          ),
          consultation_images (*)
        `
        )
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("❌ Error fetching fresh consultation data:", error);
        return { data: null, error };
      }

      if (!userData) {
        console.log("ℹ️ No user data found");
        return { data: null, error: new Error("User not found") };
      }

      // Handle both array and single object formats for user_consultations
      let consultation = null;
      if (userData.user_consultations) {
        if (Array.isArray(userData.user_consultations)) {
          consultation = userData.user_consultations[0] || null;
        } else {
          consultation = userData.user_consultations; // Single object
        }
      }

      const result = {
        user: userData,
        consultation: consultation,
        images: userData.consultation_images || [],
      };

      console.log("✅ Fresh consultation data retrieved:", {
        hasConsultation: !!consultation,
        hasProductsOffered: !!consultation?.products_offered,
        consultationType: Array.isArray(userData.user_consultations)
          ? "array"
          : "object",
      });
      return { data: result, error: null };
    } catch (error) {
      console.error("❌ Error in getFreshConsultationData:", error);
      return { data: null, error };
    }
  }

  // NOTE: Order creation removed - orders are handled by Shopify integration
  // Consultation workflow now uses consultation_status and consultation_outcome fields
  // Pharmacists update outcomes via AdminService.updateConsultationOutcome()

  // ========================================
  // IDEMPOTENCY & WEBHOOK TRACKING METHODS
  // ========================================

  /**
   * Check if a submission_id already exists in the database
   * Returns the consultation if found, null if not
   */
  static async checkSubmissionIdempotency(submissionId) {
    try {
      if (!submissionId) {
        console.warn("⚠️ No submission_id provided to check idempotency");
        return { exists: false, data: null, error: null };
      }

      // console.log("🔍 Checking submission_id:", submissionId);

      const { data, error } = await supabase
        .from("user_consultations")
        .select("*")
        .eq("submission_id", submissionId)
        .maybeSingle();

      if (error) {
        console.error("❌ Error checking submission_id:", error);
        return { exists: false, data: null, error };
      }

      if (data) {
        console.log(
          "⚠️ Duplicate submission detected! submission_id already exists:",
          submissionId
        );
        return { exists: true, data, error: null };
      }

      console.log("✅ submission_id is unique, proceeding with submission");
      return { exists: false, data: null, error: null };
    } catch (error) {
      console.error("❌ Exception in checkSubmissionIdempotency:", error);
      return { exists: false, data: null, error };
    }
  }

  /**
   * Store submission_id when creating/updating consultation
   * This should be called BEFORE the webhook is sent
   */
  static async storeSubmissionId(userId, submissionId) {
    try {
      if (!userId || !submissionId) {
        console.error("❌ Missing userId or submissionId");
        return {
          success: false,
          error: new Error("userId and submissionId are required"),
        };
      }

      console.log(
        "💾 Storing submission_id for user:",
        userId,
        "submission_id:",
        submissionId
      );

      // Get existing consultation
      const { data: existingConsultation, error: fetchError } = await supabase
        .from("user_consultations")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (fetchError) {
        console.error("❌ Error fetching consultation:", fetchError);
        return { success: false, error: fetchError };
      }

      if (!existingConsultation) {
        console.error("❌ No consultation found for user:", userId);
        return {
          success: false,
          error: new Error("Consultation not found for user"),
        };
      }

      // Update with submission_id and set webhook status to pending
      const { data, error } = await supabase
        .from("user_consultations")
        .update({
          submission_id: submissionId,
          webhook_status: "pending",
          webhook_attempts: 0,
          last_webhook_attempt: new Date().toISOString(),
        })
        .eq("id", existingConsultation.id)
        .select()
        .single();

      if (error) {
        console.error("❌ Error storing submission_id:", error);
        return { success: false, error };
      }

      console.log("✅ submission_id stored successfully");
      return { success: true, data, error: null };
    } catch (error) {
      console.error("❌ Exception in storeSubmissionId:", error);
      return { success: false, error };
    }
  }

  /**
   * Update webhook status after attempt
   * @param {string} userId - The user ID
   * @param {string} status - 'pending', 'sent', 'failed'
   * @param {number} attemptNumber - The attempt number (1, 2, 3, etc.)
   */
  static async updateWebhookStatus(userId, status, attemptNumber = 1) {
    try {
      if (!userId || !status) {
        console.error("❌ Missing userId or status");
        return {
          success: false,
          error: new Error("userId and status are required"),
        };
      }

      console.log(
        `📊 Updating webhook status for user ${userId}: ${status} (attempt ${attemptNumber})`
      );

      // Get existing consultation
      const { data: existingConsultation, error: fetchError } = await supabase
        .from("user_consultations")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (fetchError || !existingConsultation) {
        console.error(
          "❌ Error fetching consultation or consultation not found"
        );
        return {
          success: false,
          error: fetchError || new Error("Consultation not found"),
        };
      }

      // Update webhook tracking fields
      const { data, error } = await supabase
        .from("user_consultations")
        .update({
          webhook_status: status,
          webhook_attempts: attemptNumber,
          last_webhook_attempt: new Date().toISOString(),
        })
        .eq("id", existingConsultation.id)
        .select()
        .single();

      if (error) {
        console.error("❌ Error updating webhook status:", error);
        return { success: false, error };
      }

      console.log(`✅ Webhook status updated to: ${status}`);
      return { success: true, data, error: null };
    } catch (error) {
      console.error("❌ Exception in updateWebhookStatus:", error);
      return { success: false, error };
    }
  }

  /**
   * Get webhook status for a user's consultation
   */
  static async getWebhookStatus(userId) {
    try {
      const { data, error } = await supabase
        .from("user_consultations")
        .select(
          "submission_id, webhook_status, webhook_attempts, last_webhook_attempt"
        )
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("❌ Error getting webhook status:", error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error("❌ Exception in getWebhookStatus:", error);
      return { data: null, error };
    }
  }
}
